// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// HTTP transport for course-mapping trace batches, and the mapping-status probe that
// decides whether a course is worth surveying at all.

import type { CourseMappingStatus, CourseTraceBatch } from '../features/course-trace/types';
import type { TraceUploader } from './courseTraceQueue';

/**
 * Builds an uploader for the trace queue.
 *
 * Outcomes drive retry policy, so the mapping matters:
 *   - accepted: the server has it (202), or already had it — batches are idempotent.
 *   - drop: the server will never take it. 400 is malformed; 401/403 means this caller
 *     is not in that round, and no amount of retrying changes that.
 *   - retry: anything else, including offline. The batch keeps its place.
 */
export const createTraceUploader = (
  apiBaseUrl: string,
  getToken: () => string | null | undefined
): TraceUploader => {
  return async (batch: CourseTraceBatch) => {
    const token = getToken();
    if (!token) return 'retry';

    const response = await fetch(`${apiBaseUrl}/api/geodata/traces`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        batchId: batch.batchId,
        courseId: batch.courseId,
        roundId: batch.roundId,
        deviceModel: batch.deviceModel,
        points: batch.points.map((point) => ({
          holeNumber: point.holeNumber,
          lat: point.lat,
          lon: point.lon,
          accuracy: point.accuracy,
          altitude: point.altitude,
          speed: point.speed,
          clientTs: point.clientTs,
        })),
        markers: batch.markers.map((marker) => ({
          kind: marker.kind,
          holeNumber: marker.holeNumber,
          clientTs: marker.clientTs,
        })),
      }),
    });

    if (response.ok) return 'accepted';
    if (response.status === 400 || response.status === 401 || response.status === 403) {
      return 'drop';
    }
    return 'retry';
  };
};

/**
 * Resolves which course a round was played on.
 *
 * The hole screen does not carry a course id, and traces are aggregated per course, so
 * this is the one lookup capture cannot avoid. Returns null on any failure, which reads
 * as "do not capture".
 */
export const resolveRoundCourseId = async (
  apiBaseUrl: string,
  roundId: string,
  token: string | null | undefined
): Promise<string | null> => {
  if (!token) return null;
  try {
    const response = await fetch(`${apiBaseUrl}/api/rounds/${encodeURIComponent(roundId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return null;
    const body = (await response.json()) as Record<string, unknown>;
    // Round payloads vary in casing across services; accept both, and the nested course.
    const course = body.course as Record<string, unknown> | undefined;
    const candidate =
      body.courseId ?? body.CourseId ?? course?.id ?? course?.Id ?? null;
    return typeof candidate === 'string' && candidate.length > 0 ? candidate : null;
  } catch {
    return null;
  }
};

/**
 * Asks whether a course still needs traces.
 *
 * Fails closed to 'complete': if we cannot confirm a course needs surveying, we do not
 * spend four hours of the player's battery on it.
 */
export const fetchCourseMappingStatus = async (
  apiBaseUrl: string,
  courseId: string,
  token: string | null | undefined
): Promise<CourseMappingStatus> => {
  if (!token) return 'complete';
  try {
    const response = await fetch(
      `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/mapping-status`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) return 'complete';
    const body = (await response.json()) as { status?: string };
    return body.status === 'needed' ? 'needed' : 'complete';
  } catch {
    return 'complete';
  }
};
